import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import api from '@/lib/api';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';

/**
 * Public renderer for admin-created custom pages.
 * URL: /p/:slug
 * Fetches /api/pages/:slug. Only published pages are returned by the backend.
 */
export default function CustomPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true); setError(''); setPage(null);
    api.get(`/pages/${slug}`)
      .then((r) => { if (mounted) setPage(r.data); })
      .catch((e) => {
        if (!mounted) return;
        const status = e?.response?.status;
        if (status === 404) setError('not_found');
        else setError(e?.response?.data?.detail || 'Could not load this page.');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  // Set page title for SEO when page loads
  useEffect(() => {
    if (page?.meta_title || page?.title) {
      document.title = `${page.meta_title || page.title} · Kickass Course`;
    }
    // Update meta description tag if applicable
    if (page?.meta_description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
      m.setAttribute('content', page.meta_description);
    }
    return () => { document.title = 'Kickass Course'; };
  }, [page]);

  if (loading) {
    return (
      <div className="kc-container py-24 flex items-center justify-center" data-testid="custom-page-loading">
        <Loader2 className="animate-spin text-[color:var(--kc-gold-2)]" size={32}/>
      </div>
    );
  }

  if (error === 'not_found' || !page) {
    return (
      <div className="kc-container py-24 max-w-2xl text-center" data-testid="custom-page-404">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-kc mb-3">404</div>
        <h1 className="font-display text-4xl md:text-5xl uppercase mb-4">Page not found</h1>
        <p className="text-muted-kc mb-8">The page you’re looking for doesn’t exist or was unpublished.</p>
        <button onClick={() => navigate('/')} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors">
          <ArrowLeft size={16}/> Back home
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kc-container py-24 max-w-2xl text-center">
        <h1 className="font-display text-3xl uppercase mb-3">Something went wrong</h1>
        <p className="text-muted-kc">{error}</p>
      </div>
    );
  }

  const cover = page.cover_image;
  const coverSrc = cover && !cover.startsWith('http') && !cover.startsWith('/splash')
    ? `${process.env.REACT_APP_BACKEND_URL || ''}${cover}`
    : cover;

  return (
    <article className="bg-white" data-testid="custom-page">
      {/* Cover image (optional) */}
      {cover && (
        <div className="relative w-full h-[36vh] md:h-[52vh] min-h-[300px] overflow-hidden bg-ink">
          <img src={coverSrc} alt={page.title} className="absolute inset-0 w-full h-full object-cover opacity-90"/>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"/>
          <div className="absolute inset-x-0 bottom-0">
            <div className="kc-container py-10 md:py-14">
              <h1 className="font-display uppercase text-white leading-[0.92]" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }} data-testid="custom-page-title">
                {page.title}
              </h1>
            </div>
          </div>
        </div>
      )}

      {/* If no cover, render a clean editorial title block */}
      {!cover && (
        <div className="kc-container pt-16 md:pt-24 pb-6">
          <h1 className="font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }} data-testid="custom-page-title">
            {page.title}
          </h1>
        </div>
      )}

      {/* Body */}
      <div className="kc-container py-12 md:py-16 max-w-3xl">
        <div className="kc-prose font-reader" data-testid="custom-page-body">
          {page.body ? (
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => <a {...props} className="text-[color:var(--kc-gold-2)] underline hover:text-[color:var(--kc-gold)]" target={props.href?.startsWith('http') ? '_blank' : undefined} rel={props.href?.startsWith('http') ? 'noreferrer' : undefined}/>,
              }}
            >
              {page.body}
            </ReactMarkdown>
          ) : (
            <p className="text-muted-kc italic">No content yet.</p>
          )}
        </div>

        {/* CTA button (optional) */}
        {page.cta_label && page.cta_href && (
          <div className="mt-12">
            {page.cta_href.startsWith('http') ? (
              <a
                href={page.cta_href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] hover:gap-3 transition-all shadow-[0_15px_40px_-10px_rgba(212,164,55,0.4)]"
                data-testid="custom-page-cta"
              >
                {page.cta_label} <ArrowRight size={18}/>
              </a>
            ) : (
              <Link
                to={page.cta_href}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] hover:gap-3 transition-all shadow-[0_15px_40px_-10px_rgba(212,164,55,0.4)]"
                data-testid="custom-page-cta"
              >
                {page.cta_label} <ArrowRight size={18}/>
              </Link>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
